export default function DownloadPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F1F5] via-[#D1E3EA] to-[#F0F4F8]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-10 h-10 bg-[#2F7FB7] rounded-full flex items-center justify-center">
              <i className="ri-heart-pulse-line text-white text-xl"></i>
            </div>
            <span className="text-2xl font-serif text-[#2F7FB7]">RECOVER</span>
          </a>
          <a href="/" className="text-[#2F7FB7] hover:text-[#1E5A8E] transition-colors cursor-pointer flex items-center gap-2">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 space-y-6">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-3xl shadow-xl mb-6">
              <i className="ri-download-cloud-line text-5xl text-[#2F7FB7]"></i>
            </div>
            <h1 className="text-6xl md:text-7xl font-serif text-[#2F7FB7] leading-tight">
              Download RECOVER
            </h1>
            <p className="text-2xl text-[#4A5568] max-w-2xl mx-auto leading-relaxed">
              Begin your journey toward emotional wellbeing. Available now on iOS and Android.
            </p>
          </div>

          {/* Download Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Apple App Store */}
            <a
              href="https://apps.apple.com/au/app/recover-anxiety-depression/id6759235833"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group"
            >
              <div className="flex items-center justify-center w-20 h-20 bg-[#E8F1F5] rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-apple-fill text-4xl text-[#2F7FB7]"></i>
              </div>
              <h3 className="text-3xl font-serif text-[#2F7FB7] mb-4">Download for iOS</h3>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                Get RECOVER on your iPhone or iPad from the Apple App Store
              </p>
              <div className="flex items-center gap-3 text-[#2F7FB7] font-medium group-hover:gap-4 transition-all">
                <span>Download Now</span>
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </div>
              <div className="mt-6 pt-6 border-t border-[#E8F1F5]">
                <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                  <i className="ri-smartphone-line"></i>
                  <span>Requires iOS 14.0 or later</span>
                </div>
              </div>
            </a>

            {/* Google Play Store */}
            <a
              href="https://play.google.com/store/apps/details?id=com.hlrothptyltd.recoverapp"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group"
            >
              <div className="flex items-center justify-center w-20 h-20 bg-[#E8F1F5] rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-google-play-fill text-4xl text-[#2F7FB7]"></i>
              </div>
              <h3 className="text-3xl font-serif text-[#2F7FB7] mb-4">Download for Android</h3>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                Get RECOVER on your Android device from Google Play Store
              </p>
              <div className="flex items-center gap-3 text-[#2F7FB7] font-medium group-hover:gap-4 transition-all">
                <span>Download Now</span>
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </div>
              <div className="mt-6 pt-6 border-t border-[#E8F1F5]">
                <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                  <i className="ri-android-line"></i>
                  <span>Requires Android 8.0 or later</span>
                </div>
              </div>
            </a>
          </div>

          {/* Features Preview */}
          <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-10 shadow-lg mb-16">
            <h3 className="text-3xl font-serif text-[#2F7FB7] mb-8 text-center">What You'll Get</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center w-16 h-16 bg-[#E8F1F5] rounded-2xl mx-auto mb-4">
                  <i className="ri-heart-pulse-line text-2xl text-[#2F7FB7]"></i>
                </div>
                <h4 className="font-semibold text-lg text-[#2F7FB7]">Self-Assessments</h4>
                <p className="text-sm text-[#4A5568] leading-relaxed">
                  Understand your emotional wellbeing with gentle, private assessments
                </p>
              </div>
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center w-16 h-16 bg-[#E8F1F5] rounded-2xl mx-auto mb-4">
                  <i className="ri-line-chart-line text-2xl text-[#2F7FB7]"></i>
                </div>
                <h4 className="font-semibold text-lg text-[#2F7FB7]">Mood Tracking</h4>
                <p className="text-sm text-[#4A5568] leading-relaxed">
                  Monitor your progress and notice patterns over time
                </p>
              </div>
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center w-16 h-16 bg-[#E8F1F5] rounded-2xl mx-auto mb-4">
                  <i className="ri-music-2-line text-2xl text-[#2F7FB7]"></i>
                </div>
                <h4 className="font-semibold text-lg text-[#2F7FB7]">Relaxation Tools</h4>
                <p className="text-sm text-[#4A5568] leading-relaxed">
                  Access calming music, videos, and guided meditations
                </p>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-serif text-[#2F7FB7] mb-2">40+</div>
              <div className="text-sm text-[#4A5568]">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-serif text-[#2F7FB7] mb-2">100%</div>
              <div className="text-sm text-[#4A5568]">Evidence-Based</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
